<script setup lang="ts">
const { t } = useI18n();
const localePath = useLocalePath();

useHead({
  titleTemplate: () => t("seo.layouts./viber-chat-sessions.title"),
  meta: [
    { name: "description", content: () => t("seo.layouts./viber-chat-sessions.description") },
    { property: "og:title", content: () => t("seo.layouts./viber-chat-sessions.title") },
    { property: "og:description", content: () => t("seo.layouts./viber-chat-sessions.description") },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary_large_image" }
  ]
});

const faqItems = ref([
  {
    question: t('intentPages.viberChatSessions.faq.q1'),
    answer: t('intentPages.viberChatSessions.faq.a1'),
    isOpen: false
  },
  {
    question: t('intentPages.viberChatSessions.faq.q2'),
    answer: t('intentPages.viberChatSessions.faq.a2'),
    isOpen: false
  },
  {
    question: t('intentPages.viberChatSessions.faq.q3'),
    answer: t('intentPages.viberChatSessions.faq.a3'),
    isOpen: false
  },
  {
    question: t('intentPages.viberChatSessions.faq.q4'),
    answer: t('intentPages.viberChatSessions.faq.a4'),
    isOpen: false
  }
]);

onMounted(() => {
  document.querySelector("body")?.scrollIntoView();
});
</script>

<template>
  <div class="provider-intent-page">
    
    <!-- Hero Banner -->
    <div class="main-banner bg-gradient-to-br from-blue-50 to-blue-100 pt-[120px] pb-20 px-4">
      <div class="container mx-auto max-w-5xl text-center">
        <h1 class="text-4xl md:text-6xl font-extrabold text-[#3070b6] mb-6 leading-tight">
          {{ $t('intentPages.viberChatSessions.heroTitle') }}
        </h1>
        <p class="text-xl md:text-2xl text-gray-700 max-w-4xl mx-auto mb-6 leading-relaxed">
          {{ $t('intentPages.viberChatSessions.heroSubtitle') }}
        </p>
        <p class="text-lg font-bold text-gray-800 bg-white inline-block px-4 py-2 rounded-lg shadow-sm mb-10 border border-gray-200">
          ⚡ {{ $t('intentPages.viberChatSessions.heroStats') }}
        </p>
        <div class="flex justify-center flex-wrap gap-4">
          <a href="https://omni.smsbat.com/auth/login" target="_blank" class="nav-link button filled px-8 py-4 text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transition text-white bg-[#3070b6] hover:bg-blue-700">
            {{ $t('intentPages.viberChatSessions.cta.buttonPrimary') }}
          </a>
          <NuxtLink :to="localePath('/pricing')" class="nav-link button outlined px-8 py-4 text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transition border-2 border-[#3070b6] text-[#3070b6] bg-transparent hover:bg-blue-50">
            {{ $t('intentPages.viberChatSessions.cta.buttonSecondary') }}
          </NuxtLink>
        </div>
      </div>
    </div>

    <!-- Session Rules / How it Works -->
    <div class="page-section py-20 bg-white">
      <div class="container mx-auto max-w-5xl px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{{ $t('intentPages.viberChatSessions.rules.title') }}</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">{{ $t('intentPages.viberChatSessions.rules.subtitle') }}</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div class="bg-blue-50/50 p-8 rounded-2xl border border-blue-100 hover:shadow-md transition text-center flex flex-col items-center">
            <div class="w-16 h-16 bg-[#3070b6] text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6">24h</div>
            <h3 class="text-2xl font-bold text-gray-900 mb-4">{{ $t('intentPages.viberChatSessions.rules.rule1Title') }}</h3>
            <p class="text-lg text-gray-700">{{ $t('intentPages.viberChatSessions.rules.rule1Desc') }}</p>
          </div>
          
          <div class="bg-blue-50/50 p-8 rounded-2xl border border-blue-100 hover:shadow-md transition text-center flex flex-col items-center">
            <div class="w-16 h-16 bg-[#3070b6] text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6">60</div>
            <h3 class="text-2xl font-bold text-gray-900 mb-4">{{ $t('intentPages.viberChatSessions.rules.rule2Title') }}</h3>
            <p class="text-lg text-gray-700">{{ $t('intentPages.viberChatSessions.rules.rule2Desc') }}</p>
          </div>
          
          <div class="bg-red-50 p-8 rounded-2xl border border-red-100 hover:shadow-md transition text-center flex flex-col items-center">
            <div class="w-16 h-16 bg-red-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6">10</div>
            <h3 class="text-2xl font-bold text-gray-900 mb-4">{{ $t('intentPages.viberChatSessions.rules.rule3Title') }}</h3>
            <p class="text-lg text-gray-700">{{ $t('intentPages.viberChatSessions.rules.rule3Desc') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Pricing Section -->
    <div class="page-section py-20 bg-gray-50 border-y border-gray-100">
      <div class="container mx-auto px-4 max-w-4xl">
         <div class="text-center mb-12">
           <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{{ $t('intentPages.viberChatSessions.pricing.title') }}</h2>
           <p class="text-xl text-gray-600">{{ $t('intentPages.viberChatSessions.pricing.subtitle') }}</p>
         </div>

         <!-- Re-use existing table component since rates are identical to chatbot session rates often, but user wanted specific table -->
         <div class="mb-10 mt-8">
            <BMChatSessionsRatesTable />
         </div>
         
         <div class="mt-4 text-center">
          <NuxtLink :to="localePath('/pricing')" class="text-[#3070b6] hover:text-blue-800 font-bold underline transition">
            {{ $t('intentPages.viberChatSessions.pricing.fullPricingLink') }}
          </NuxtLink>
         </div>
      </div>
    </div>

    <!-- Omnichannel Ecosystem -->
    <div class="page-section py-20 bg-white">
      <div class="container mx-auto max-w-5xl px-4">
        <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">{{ $t('intentPages.viberChatSessions.ecosystem.title') }}</h2>
        
        <div class="space-y-16">
          
          <!-- Web Panel -->
          <div class="flex flex-col md:flex-row items-center gap-12">
            <div class="md:w-1/2">
               <h3 class="text-2xl font-bold text-gray-900 mb-4">{{ $t('intentPages.viberChatSessions.ecosystem.panelTitle') }}</h3>
               <p class="text-lg text-gray-700 leading-relaxed mb-6">{{ $t('intentPages.viberChatSessions.ecosystem.panelDesc') }}</p>
               <ul class="space-y-3">
                 <li class="flex items-center text-gray-600"><span class="text-[#3070b6] mr-2">✓</span> {{ $t('intentPages.viberChatSessions.ecosystem.panelFeature1') }}</li>
                 <li class="flex items-center text-gray-600"><span class="text-[#3070b6] mr-2">✓</span> {{ $t('intentPages.viberChatSessions.ecosystem.panelFeature2') }}</li>
                 <li class="flex items-center text-gray-600"><span class="text-[#3070b6] mr-2">✓</span> {{ $t('intentPages.viberChatSessions.ecosystem.panelFeature3') }}</li>
               </ul>
            </div>
            <div class="md:w-1/2 rounded-2xl shadow-xl overflow-hidden border border-gray-200">
               <NuxtImg src="/images/chat/tablet-1.png" width="1024" height="768" alt="Web Panel" format="webp" loading="lazy" class="w-full h-auto object-cover" />
            </div>
          </div>

          <!-- Integrations (iFrame / API) -->
          <div class="flex flex-col md:flex-row-reverse items-center gap-12">
            <div class="md:w-1/2">
               <h3 class="text-2xl font-bold text-gray-900 mb-4">{{ $t('intentPages.viberChatSessions.ecosystem.integrationTitle') }}</h3>
               <p class="text-lg text-gray-700 leading-relaxed mb-6">{{ $t('intentPages.viberChatSessions.ecosystem.integrationDesc') }}</p>
               
               <div class="bg-gray-50 border-l-4 border-green-500 p-4 rounded-r-lg mb-6">
                 <p class="text-gray-800 font-medium">{{ $t('intentPages.viberChatSessions.ecosystem.iframeNote') }}</p>
               </div>
               
               <NuxtLink to="https://help.smsbat.com/integrations/" target="_blank" class="text-[#3070b6] font-bold inline-flex items-center hover:text-blue-800 transition">
                 {{ $t('intentPages.viberChatSessions.ecosystem.integrationLink') }} <span class="ml-2">→</span>
               </NuxtLink>
            </div>
            <div class="md:w-1/2 grid grid-cols-2 gap-4">
               <div class="bg-gray-50 aspect-square rounded-2xl flex flex-col items-center justify-center p-6 border border-gray-100 shadow-sm hover:shadow-md transition">
                 <i class="pi pi-desktop text-4xl text-[#3070b6] mb-4"></i>
                 <span class="font-bold text-gray-800">{{ $t('intentPages.viberChatSessions.ecosystem.iconNative') }}</span>
               </div>
               <div class="bg-gray-50 aspect-square rounded-2xl flex flex-col items-center justify-center p-6 border border-gray-100 shadow-sm hover:shadow-md transition">
                 <i class="pi pi-code text-4xl text-[#3070b6] mb-4"></i>
                 <span class="font-bold text-gray-800">{{ $t('intentPages.viberChatSessions.ecosystem.iconIframe') }}</span>
               </div>
               <div class="bg-gray-50 aspect-square rounded-2xl flex flex-col items-center justify-center p-6 border border-gray-100 shadow-sm hover:shadow-md transition">
                 <i class="pi pi-bolt text-4xl text-[#3070b6] mb-4"></i>
                 <span class="font-bold text-gray-800">{{ $t('intentPages.viberChatSessions.ecosystem.iconN8n') }}</span>
               </div>
               <div class="bg-gray-50 aspect-square rounded-2xl flex flex-col items-center justify-center p-6 border border-gray-100 shadow-sm hover:shadow-md transition">
                 <i class="pi pi-database text-4xl text-[#3070b6] mb-4"></i>
                 <span class="font-bold text-gray-800">{{ $t('intentPages.viberChatSessions.ecosystem.iconApi') }}</span>
               </div>
            </div>
          </div>
          
        </div>
      </div>
    </div>

    <!-- FAQ -->
    <div class="page-section py-20 bg-gray-50">
      <div class="container mx-auto px-4">
        <PopularQuestions :title="$t('intentPages.viberChatSessions.faq.title')" :questions="faqItems" />
      </div>
    </div>

    <!-- Contact CTA -->
    <ContactForm :indexbat-form-id="74" />
  </div>
</template>

<style scoped>
.provider-intent-page {
  font-family: 'Inter', sans-serif;
}
.provider-intent-page h1, .provider-intent-page h2, .provider-intent-page h3 {
  letter-spacing: -0.02em;
}
</style>
